// Payment controls for bills — recording a payment is separate from approving a bill (docs/payment-status-gap.md).
//   PaymentFields  — Paid amount / Paid date / Proof / Note, controlled. Shared by the scanner's "Already paid" box,
//                    the card's Process form, and the two panels below, so every path asks the same four things.
//   BillPaymentPanel — one existing bill on the parcel card: Mark paid · Edit payment · Mark unpaid (utils/paymentRecord).
//   MarkAllPaid    — every unpaid bill of one collector-year at once (backfill years). Amount = each bill's billed.
import React, { useState } from 'react';
import MoneyInput from './MoneyInput';
import { PAID_PROOFS, proofLabel, recordPayment, clearPayment } from '../utils/paymentRecord';
import { TAX_BILL_STATUS } from '../constants/statuses';

const fmt = (n) => (n == null || n === '' || !Number.isFinite(Number(n)) ? '—'
  : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtMDY = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d || '')); return m ? `${m[2]}/${m[3]}/${m[1]}` : ''; };
const moneyIn = (n) => (n == null || !Number.isFinite(Number(n)) ? '' : Number(n).toFixed(2));

// One line describing a recorded payment: "Paid $6,083.98 · 03/01/2025 · proof: Receipt · <note>".
export function paymentSummary(p) {
  if (!p) return '';
  const proof = p.proof ? `proof: ${proofLabel(p.proof)}` : (p.source === 'bill' ? 'shown on the bill' : 'no proof recorded');
  return ['Paid' + (p.amount != null ? ` ${fmt(p.amount)}` : ''), p.at ? fmtMDY(p.at) : 'date not known', proof, p.note || '']
    .filter(Boolean).join(' · ');
}

// value: { amount, date, proof, note }; onChange(next). showAmount=false hides the amount (bulk: each bill's billed).
export function PaymentFields({ value, onChange, s, c, showAmount = true, proofOptional = false }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const lbl = { fontSize: '9px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' };
  const inp = { ...s.input, fontSize: '12px', padding: '4px 8px' };
  return (
    // Wraps to fit: the scanner's review column is narrow, the card's is wide.
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px 10px', alignItems: 'end' }}>
      {showAmount && (
        <div><div style={lbl}>Paid amount</div>
          <MoneyInput value={value.amount ?? ''} onChange={(clean) => set('amount', clean)}
            onBlur={() => set('amount', value.amount === '' || value.amount == null ? '' : moneyIn(Number(value.amount)))}
            placeholder="$0.00" style={{ ...inp, width: '100%', fontFamily: 'monospace', textAlign: 'right' }} /></div>
      )}
      <div><div style={lbl}>Paid date (optional)</div>
        <input type="date" value={value.date || ''} onChange={e => set('date', e.target.value)} style={{ ...inp, width: '100%' }} /></div>
      <div><div style={lbl}>Proof{proofOptional ? ' (optional)' : ''}</div>
        <select value={value.proof || ''} onChange={e => set('proof', e.target.value)} style={{ ...inp, width: '100%' }}>
          <option value="">{proofOptional ? '— shown on the bill —' : '— pick —'}</option>
          {PAID_PROOFS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select></div>
      <div><div style={lbl}>Note (optional)</div>
        <input type="text" maxLength={300} value={value.note || ''} onChange={e => set('note', e.target.value)} placeholder="short note" style={{ ...inp, width: '100%' }} /></div>
    </div>
  );
}

// bill: { id, status, billed, paid: { source, amount, at, proof, note, recordedBy } | null }
export function BillPaymentPanel({ bill, entityName, staffName, onChanged, s, c }) {
  const [mode, setMode] = useState(null);          // null | 'pay' | 'unpay'
  const [form, setForm] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);            // { ok, text }
  const paid = bill.paid;
  const approved = bill.status === TAX_BILL_STATUS.APPROVED;
  const filed = bill.status === TAX_BILL_STATUS.FILED;

  const open = () => {
    setMsg(null);
    setForm(paid
      ? { amount: moneyIn(paid.amount), date: paid.at || '', proof: paid.proof || '', note: paid.note || '' }
      : { amount: moneyIn(bill.billed), date: '', proof: '', note: '' });
    setMode('pay');
  };
  const run = async (fn, done) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      setMode(null); setReason('');
      setMsg({ ok: true, text: done(r) });
      onChanged && onChanged(r);
    } catch (e) { setMsg({ ok: false, text: e.message || String(e) }); }
    finally { setBusy(false); }
  };
  const save = () => run(
    () => recordPayment({ billId: bill.id, ...form, staffName, entityName }),
    (r) => `✓ Payment ${paid ? 'updated' : 'recorded'}${r.statusTo !== r.statusFrom ? ' — the bill moved to Filed (a paid bill is never transmitted)' : ''}. Nothing billed changed.`);
  const unpay = () => run(
    () => clearPayment({ billId: bill.id, staffName, entityName, reason }),
    (r) => `✓ Marked unpaid${r.statusTo !== r.statusFrom ? ' — the bill is back at Approved (awaiting transmittal)' : ''}.`);

  const box = { marginTop: '8px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${c.border}`, width: '100%' };
  const btn = { ...s.btnGhost, padding: '3px 10px', fontSize: '11px', whiteSpace: 'nowrap' };
  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: paid ? '#059669' : c.muted }}>
          {paid ? `✓ ${paymentSummary(paid)}` : 'Payment: not marked paid'}
        </span>
        {paid?.recordedBy && <span style={{ fontSize: '10.5px', color: c.muted }}>recorded by {paid.recordedBy}</span>}
        {mode == null && <button type="button" style={btn} onClick={open}>{paid ? 'Edit payment' : 'Mark paid'}</button>}
        {mode == null && paid && <button type="button" style={btn} onClick={() => { setMsg(null); setReason(''); setMode('unpay'); }}>Mark unpaid</button>}
      </div>
      {mode === 'pay' && form && (
        <div style={{ marginTop: '8px' }}>
          <PaymentFields value={form} onChange={setForm} s={s} c={c} />
          <div style={{ fontSize: '10.5px', color: c.muted, margin: '6px 0' }}>
            Billed {fmt(bill.billed)}. Saving changes only the payment — the bill is not sent back to Approval.
            {approved && !paid ? ' This bill is Approved, so it moves to Filed: a paid bill is never transmitted.' : ''}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" disabled={busy || !form.proof} onClick={save}
              style={{ ...s.btn, padding: '4px 12px', fontSize: '11px', opacity: (busy || !form.proof) ? 0.5 : 1 }}>{busy ? 'Saving…' : paid ? 'Save payment' : 'Mark paid'}</button>
            <button type="button" disabled={busy} onClick={() => setMode(null)} style={btn}>Cancel</button>
          </div>
        </div>
      )}
      {mode === 'unpay' && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ fontSize: '10.5px', color: c.muted, marginBottom: '6px' }}>
            Clears the recorded payment. Nothing billed changes.
            {filed ? ' If this bill was filed by marking it paid, it returns to Approved (awaiting transmittal).' : ''}
          </div>
          <input type="text" maxLength={300} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why? (optional)"
            style={{ ...s.input, fontSize: '12px', padding: '4px 8px', marginBottom: '6px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" disabled={busy} onClick={unpay} style={{ ...s.btn, padding: '4px 12px', fontSize: '11px', opacity: busy ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Mark unpaid'}</button>
            <button type="button" disabled={busy} onClick={() => setMode(null)} style={btn}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <div style={{ fontSize: '11px', fontWeight: 600, marginTop: '6px', color: msg.ok ? '#16a34a' : '#dc2626' }}>{msg.text}</div>}
    </div>
  );
}

// bills: [{ id, label, status, billed }] — the collector-year's live, unpaid, not-returned bills. Offered for two or more
// (one bill has its own Mark paid); stays mounted after a run so its result line is not lost when the list empties.
export function MarkAllPaid({ bills, entityName, staffName, onChanged, s, c, wrapStyle }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: '', proof: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  if (bills.length < 2 && !open && !msg) return null;
  const go = async () => {
    setBusy(true); setMsg(null);
    const fails = []; let moved = 0, done = 0;
    for (const b of bills) {
      try {
        const r = await recordPayment({ billId: b.id, amount: b.billed, date: form.date, proof: form.proof, note: form.note,
          staffName, entityName: `${entityName} · ${b.label}` });
        done++; if (r.statusTo !== r.statusFrom) moved++;
      } catch (e) { fails.push(`${b.label}: ${e.message || e}`); }
    }
    setBusy(false); setOpen(false);
    setMsg({ ok: !fails.length, text: `${done} of ${bills.length} marked paid${moved ? ` (${moved} moved from Approved to Filed)` : ''}.${fails.length ? ' Not marked — ' + fails.join(' · ') : ''}` });
    onChanged && onChanged();
  };
  const btn = { ...s.btnGhost, padding: '4px 10px', fontSize: '11px', whiteSpace: 'nowrap' };
  return (
    <div style={wrapStyle}>
      {!open && bills.length > 1 && (
        <button type="button" style={btn} onClick={() => { setMsg(null); setOpen(true); }}>✓ Mark all installments paid ({bills.length})</button>
      )}
      {open && (
        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${c.border}` }}>
          <div style={{ fontSize: '11px', color: c.text, marginBottom: '6px' }}>
            Marks {bills.map(b => `${b.label} (${fmt(b.billed)})`).join(', ')} paid at each one's billed amount. Only the payment is recorded;
            Approved bills move to Filed.
          </div>
          <PaymentFields value={form} onChange={setForm} s={s} c={c} showAmount={false} />
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
            <button type="button" disabled={busy || !form.proof} onClick={go}
              style={{ ...s.btn, padding: '4px 12px', fontSize: '11px', opacity: (busy || !form.proof) ? 0.5 : 1 }}>{busy ? 'Saving…' : `Mark ${bills.length} paid`}</button>
            <button type="button" disabled={busy} onClick={() => setOpen(false)} style={btn}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <div style={{ fontSize: '11px', fontWeight: 600, marginTop: '4px', color: msg.ok ? '#16a34a' : '#dc2626' }}>{msg.text}</div>}
    </div>
  );
}
